import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Sparkles,
  Camera,
  X,
  Download,
  RefreshCw,
  Loader2,
  ImageIcon,
  Plus,
  DoorClosed,
  DoorOpen,
  UtensilsCrossed,
  BedDouble,
  Tv,
  Film,
  Armchair,
  Moon,
  Layers,
  Shirt,
  Diamond,
  Gem,
  Circle,
  Star,
  Maximize2,
  LogOut,
  LayoutGrid,
  Dices,
  CheckCircle2,
  Sofa,
  Lamp,
  Palette,
  Frame,
  Wand2,
  Paintbrush,
} from "lucide-react";
import { Link } from "react-router-dom";
import "./App.css";
import { resilientFetch } from "./lib/resilientFetch";
import SitePinDesigner, { LETTER_COLORS } from "./SitePinDesigner";
import Lightbox from "./Lightbox";
import FeedbackWidget from "./FeedbackWidget";
import { useAuth, authHeaders } from "./auth";
import { useCreditAlert } from "./CreditAlert";

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "")}/api`;

const SCENE_ICON = {
  wardrobe: Shirt,
  kitchen: UtensilsCrossed,
  "bed-back": BedDouble,
  "tv-unit": Tv,
  "home-theater": Film,
  "living-room": Armchair,
  bedroom: Moon,
  ceiling: Layers,
  "main-door": DoorOpen,
  "bedroom-door": DoorClosed,
};

const STYLES = [
  { id: "modern", label: "Modern", icon: Diamond },
  { id: "traditional", label: "Traditional", icon: Gem },
  { id: "minimal", label: "Minimal", icon: Circle },
  { id: "aesthetic", label: "Aesthetic", icon: Star },
];

// Warm, reassuring messages shown while we wait for the render to land in the
// gallery after a slow-proxy timeout. Rotates every ~4 s so the user feels we're
// with them, not abandoning them.
const RECOVERY_LINES = [
  {
    h: "Just adding the finishing touches…",
    s: "Your render is on its way — please hold on a moment.",
  },
  {
    h: "Almost there — saving your design.",
    s: "Sometimes a beautiful render needs a few extra seconds.",
  },
  {
    h: "Polishing the lighting and shadows…",
    s: "We're making sure every detail feels magazine-grade.",
  },
  {
    h: "Wrapping up your visualisation…",
    s: "It will appear here the moment it's ready.",
  },
  {
    h: "Hang tight — final pass in progress.",
    s: "Architect-grade quality is worth the small wait.",
  },
];

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/* ------------------- IDEMPOTENCY KEY (global) ------------------- */
// Mints a per-user-action key so retried POSTs to /generate resolve to
// the SAME billed job instead of starting duplicates. crypto.randomUUID
// is unavailable on non-HTTPS origins and older browsers — hence the
// timestamp+random fallback.
function newIdemKey() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch {}
  return `k-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/* ----------------- RETRY-SUCCESS TOAST (global) ----------------- */
// Listens for the `hedaply:retry-success` event dispatched by
// resilientFetch and shows a small reassurance toast for ~2.5s.
// Builds staff trust: silent retries become a tiny visible "we got
// you" moment instead of an invisible network blip.
function RetryToast() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onRetry = () => {
      setVisible(true);
      const t = setTimeout(() => setVisible(false), 2500);
      return () => clearTimeout(t);
    };
    window.addEventListener("hedaply:retry-success", onRetry);
    return () => window.removeEventListener("hedaply:retry-success", onRetry);
  }, []);
  if (!visible) return null;
  return (
    <div
      data-testid="retry-toast"
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed bottom-5 left-1/2 z-[100] -translate-x-1/2 animate-fade-up"
    >
      <div className="flex items-center gap-2 rounded-full border border-heda-gold/30 bg-heda-ink/90 px-4 py-2 text-[12.5px] font-medium text-white shadow-lg backdrop-blur">
        <CheckCircle2 size={14} className="text-heda-gold" />
        <span>Auto-retried · all good</span>
      </div>
    </div>
  );
}

/** Downscale + re-encode an image (dataURL or File) to keep payload small.
 *  Max dimension 1600px, JPEG quality 0.85 — safe for mobile uploads. */
function compressImage(srcDataUrl, maxDim = 1600, quality = 0.85) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      const longest = Math.max(width, height);
      if (longest > maxDim) {
        const scale = maxDim / longest;
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => resolve(srcDataUrl); // fallback to original
    img.src = srcDataUrl;
  });
}

/* ----------------------------- HEADER ----------------------------- */
function Header({ user, onLogout }) {
  return (
    <header data-testid="app-header" className="w-full pt-6 pb-5 md:pt-10 md:pb-7">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 md:px-8">
        <div className="flex flex-1 items-center justify-center gap-4 sm:gap-6">
          <img
            src="/heda-logo.png"
            alt="HEDA Plywoods"
            className="h-20 w-auto sm:h-24 md:h-28"
            data-testid="heda-logo"
          />
          <div className="hidden h-20 w-px bg-heda-line sm:block md:h-24" />
          <div className="hidden text-left sm:block">
            <h1 className="font-display text-[24px] font-bold tracking-tight text-heda-ink md:text-[30px]">
              Interior Visualizer
            </h1>
            <p className="mt-1 text-[12.5px] text-heda-mute">
              Create stunning designs with your products
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Link
            to="/admin"
            data-testid="open-gallery"
            className="inline-flex items-center gap-1.5 rounded-full border border-heda-line bg-white px-3 py-1.5 text-[12.5px] font-medium text-heda-ink shadow-sm transition hover:border-heda-gold hover:text-heda-gold"
            title="Open gallery"
          >
            <LayoutGrid size={13} />
            <span className="hidden sm:inline">Gallery</span>
          </Link>
          <button
            data-testid="logout-btn"
            onClick={onLogout}
            className="inline-flex items-center gap-1.5 rounded-full bg-heda-ink px-3 py-1.5 text-[12.5px] font-medium text-white transition hover:bg-black"
            title={user ? `Logout (${user.name})` : "Logout"}
          >
            <LogOut size={13} />
            <span className="hidden md:inline">Logout</span>
          </button>
        </div>
      </div>
      <div className="mt-3 text-center sm:hidden">
        <h1 className="font-display text-[22px] font-bold tracking-tight text-heda-ink">
          Interior Visualizer
        </h1>
        <p className="mt-0.5 text-[12px] text-heda-mute">
          Create stunning designs with your products
        </p>
      </div>
    </header>
  );
}

/* --------------------------- SCENE GRID --------------------------- */
function SceneGrid({ scenes, value, onChange }) {
  return (
    <div
      data-testid="scene-grid"
      className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4"
    >
      {scenes.map((s) => {
        const Icon = SCENE_ICON[s.id] || ImageIcon;
        const active = value === s.id;
        return (
          <button
            type="button"
            key={s.id}
            data-testid={`scene-${s.id}`}
            onClick={() => onChange(s.id)}
            className={[
              "group flex aspect-square flex-col items-center justify-center rounded-2xl border px-2 py-4 transition-all duration-200",
              active
                ? "border-heda-gold bg-heda-gold-tint shadow-[0_0_0_1px_rgba(184,134,11,.35)_inset]"
                : "border-heda-line bg-white hover:border-heda-gold/40 hover:shadow-sm",
            ].join(" ")}
          >
            <Icon
              size={28}
              strokeWidth={1.5}
              className={active ? "text-heda-gold" : "text-heda-ink/85"}
            />
            <span
              className={[
                "mt-3 text-center text-[12.5px] leading-tight",
                active ? "font-semibold text-heda-ink" : "font-medium text-heda-ink/80",
              ].join(" ")}
            >
              {s.name}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* ------------------------ SITE REFERENCE (OPTIONAL) ------------------- */
function SiteSample({ site, onSet, onClear, onOpenCamera }) {
  const inputRef = useRef(null);

  const handleFiles = async (files) => {
    const f = files && files[0];
    if (!f || !f.type.startsWith("image/")) return;
    const raw = await fileToBase64(f);
    const compressed = await compressImage(raw);
    onSet(compressed);
  };

  return (
    <div data-testid="site-sample" className="mb-3">
      {site ? (
        <div className="flex items-center gap-2 rounded-lg border border-heda-line bg-white p-1.5">
          <div className="relative h-12 w-20 shrink-0 overflow-hidden rounded-md bg-heda-cream">
            <img src={site} alt="Site" className="h-full w-full object-cover" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[11.5px] font-semibold text-heda-ink">
              Site reference attached
              <span className="ml-1 inline-block rounded-sm bg-heda-gold/15 px-1 py-[1px] text-[8.5px] font-medium uppercase tracking-wider text-heda-gold/90 align-middle">
                Beta
              </span>
            </div>
            <div className="truncate text-[10.5px] text-heda-mute">
              AI will fit your samples into this real space.
            </div>
          </div>
          <button
            data-testid="site-remove"
            type="button"
            onClick={onClear}
            className="rounded-full p-1.5 text-heda-mute hover:bg-heda-cream hover:text-heda-ink"
            title="Remove site image"
          >
            <X size={12} />
          </button>
        </div>
      ) : (
        <details className="group rounded-lg">
          <summary
            data-testid="site-toggle"
            className="flex cursor-pointer items-center gap-1.5 text-[10.5px] font-medium uppercase tracking-wider text-heda-mute hover:text-heda-ink"
          >
            <Plus size={11} strokeWidth={2.4} />
            Add site image
            <span className="ml-1 inline-block rounded-sm bg-heda-gold/15 px-1 py-[1px] text-[8px] font-medium uppercase tracking-wider text-heda-gold/90">
              Beta
            </span>
            <span className="ml-1 normal-case tracking-normal text-heda-mute/80">
              (optional — show me on a real wall / half-done room)
            </span>
          </summary>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              data-testid="site-add-camera"
              onClick={onOpenCamera}
              className="inline-flex items-center gap-1 rounded-full border border-heda-line bg-white px-2.5 py-1 text-[11px] text-heda-ink hover:border-heda-gold"
            >
              <Camera size={11} /> Take photo
            </button>
            <button
              type="button"
              data-testid="site-add-upload"
              onClick={() => inputRef.current?.click()}
              className="inline-flex items-center gap-1 rounded-full border border-heda-line bg-white px-2.5 py-1 text-[11px] text-heda-ink hover:border-heda-gold"
            >
              <ImageIcon size={11} /> Upload
            </button>
          </div>
        </details>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        data-testid="site-upload-input"
        onChange={(e) => handleFiles(e.target.files)}
      />
    </div>
  );
}


/* ------------------------ PRODUCT SAMPLES ------------------------- */
function ProductSamples({ products, onAdd, onRemove, onSetMain, onSetScale, onOpenCamera, max = 4, siteActive = false }) {
  const inputRef = useRef(null);
  const [menu, setMenu] = useState(false);

  const handleFiles = async (files) => {
    const arr = Array.from(files).slice(0, max - products.length);
    const out = [];
    for (const f of arr) {
      if (!f.type.startsWith("image/")) continue;
      const raw = await fileToBase64(f);
      const compressed = await compressImage(raw);
      out.push(compressed);
    }
    if (out.length) onAdd(out);
  };

  return (
    <div data-testid="product-samples">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-[14px] text-heda-ink/90">
          Your Product Samples{" "}
          <span className="text-heda-mute">
            ({products.length}/{max})
          </span>
        </div>
      </div>
      {products.length > 0 && (
        <p className="mb-3 text-[11.5px] text-heda-mute">
          Tap a sample to mark it as <span className="font-semibold text-heda-gold">MAIN</span>. Use the scale toggle to tell the AI if it&apos;s a small <span className="font-semibold">Cutout</span> swatch or a <span className="font-semibold">Full Sheet</span> miniature.
        </p>
      )}

      <div className="flex flex-wrap gap-3">
        {products.map((p, i) => (
          <div key={p.src || `slot-${i}`} className="flex flex-col items-stretch gap-1.5">
            <button
              type="button"
              data-testid={`product-thumb-${i}`}
              onClick={() => onSetMain(i)}
              className={[
                "group relative h-24 w-24 overflow-hidden rounded-xl border bg-heda-cream shadow-sm transition-all",
                p.role === "main"
                  ? "border-heda-gold ring-2 ring-heda-gold/40"
                  : "border-heda-line hover:border-heda-gold/50",
              ].join(" ")}
            >
              <img src={p.src} alt={`Sample ${i + 1}`} className="h-full w-full object-cover" />
              <span
                data-testid={`product-role-${i}`}
                className={[
                  "absolute left-1 top-1 rounded-md px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider",
                  p.role === "main"
                    ? "bg-heda-gold text-white"
                    : "bg-white/90 text-heda-ink/70",
                ].join(" ")}
              >
                {p.role === "main" ? "Main" : "Sub"}
              </span>
              <span
                role="button"
                tabIndex={-1}
                data-testid={`product-remove-${i}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(i);
                }}
                className="absolute right-1 top-1 flex h-5 w-5 cursor-pointer items-center justify-center rounded-full bg-heda-ink/85 text-white opacity-0 transition group-hover:opacity-100"
                aria-label="Remove sample"
              >
                <X size={12} />
              </span>
              {siteActive && (() => {
                const letter = String.fromCharCode(65 + i);
                const color = LETTER_COLORS[letter] || LETTER_COLORS.A;
                return (
                  <span
                    data-testid={`product-letter-${i}`}
                    className="absolute bottom-1 right-1 flex h-4 w-4 items-center justify-center rounded-full text-[9.5px] font-bold text-white shadow ring-1 ring-white/70"
                    style={{ backgroundColor: color.bg }}
                    aria-label={`Material ${letter}`}
                    title={`Material ${letter}`}
                  >
                    {letter}
                  </span>
                );
              })()}
            </button>

            {/* Scale toggle: Cutout vs Full Sheet */}
            <div
              data-testid={`product-scale-${i}`}
              className="grid w-24 grid-cols-2 overflow-hidden rounded-md border border-heda-line bg-white text-[9.5px] font-bold uppercase tracking-wider"
            >
              <button
                type="button"
                data-testid={`product-scale-cutout-${i}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onSetScale(i, "cutout");
                }}
                title="Small physical cutout swatch — AI will tile naturally on full panel"
                className={[
                  "px-1 py-1 transition",
                  (p.scale_type || "cutout") === "cutout"
                    ? "bg-heda-olive text-white"
                    : "text-heda-ink/60 hover:bg-heda-olive-tint",
                ].join(" ")}
              >
                Cutout
              </button>
              <button
                type="button"
                data-testid={`product-scale-sheet-${i}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onSetScale(i, "sheet");
                }}
                title="Miniature photo of the full 8×4 ft sheet — AI will map it directly"
                className={[
                  "px-1 py-1 transition",
                  p.scale_type === "sheet"
                    ? "bg-heda-olive text-white"
                    : "text-heda-ink/60 hover:bg-heda-olive-tint",
                ].join(" ")}
              >
                Sheet
              </button>
            </div>
          </div>
        ))}

        {products.length < max && (
          <div className="relative">
            <button
              type="button"
              data-testid="add-sample-btn"
              onClick={() => setMenu((v) => !v)}
              onBlur={() => setTimeout(() => setMenu(false), 150)}
              className="flex h-24 w-24 flex-col items-center justify-center rounded-xl border-2 border-dashed border-heda-line bg-white text-heda-mute transition hover:border-heda-gold hover:text-heda-gold"
            >
              <Plus size={22} strokeWidth={1.6} />
              <span className="mt-1 text-[11px] uppercase tracking-wider">Add</span>
            </button>
            {menu && (
              <div
                data-testid="add-sample-menu"
                className="absolute left-0 top-full z-20 mt-2 w-44 overflow-hidden rounded-xl border border-heda-line bg-white shadow-lg"
                onMouseDown={(e) => e.preventDefault()}
              >
                <button
                  data-testid="add-from-camera"
                  onClick={() => {
                    setMenu(false);
                    onOpenCamera();
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-[13px] text-heda-ink hover:bg-heda-gold-tint"
                >
                  <Camera size={15} className="text-heda-gold" /> Take photo
                </button>
                <button
                  data-testid="add-from-upload"
                  onClick={() => {
                    setMenu(false);
                    inputRef.current?.click();
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-[13px] text-heda-ink hover:bg-heda-gold-tint"
                >
                  <ImageIcon size={15} className="text-heda-gold" /> Upload image
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        data-testid="upload-input"
        onChange={(e) => handleFiles(e.target.files)}
      />
    </div>
  );
}

/* ---------------------------- STYLES ------------------------------ */
function StyleGrid({ selected, onToggle }) {
  return (
    <div data-testid="style-grid" className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {STYLES.map((s) => {
        const Icon = s.icon;
        const active = selected.includes(s.id);
        return (
          <button
            key={s.id}
            type="button"
            data-testid={`style-${s.id}`}
            onClick={() => onToggle(s.id)}
            className={[
              "flex flex-col items-center justify-center rounded-xl border px-2 py-3 transition-all duration-200",
              active
                ? "border-heda-olive bg-heda-olive-tint shadow-[0_0_0_1px_rgba(107,122,61,.4)_inset]"
                : "border-heda-line bg-white hover:border-heda-olive/40",
            ].join(" ")}
          >
            <Icon
              size={18}
              strokeWidth={1.5}
              className={active ? "text-heda-olive" : "text-heda-ink/80"}
            />
            <span
              className={[
                "mt-2 text-[11px] font-semibold uppercase tracking-[0.14em]",
                active ? "text-heda-olive" : "text-heda-ink/75",
              ].join(" ")}
            >
              {s.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* -------------------------- CAMERA MODAL -------------------------- */
function CameraModal({ open, onClose, onCapture }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const trackRef = useRef(null);
  const [err, setErr] = useState("");
  const [zoom, setZoom] = useState(1);
  const [zoomCaps, setZoomCaps] = useState(null); // {min,max,step} when supported

  useEffect(() => {
    if (!open) return;
    let active = true;
    setZoom(1);
    setZoomCaps(null);
    setErr("");
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1080 },
            height: { ideal: 1440 }, // portrait 3:4 hint
          },
          audio: false,
        });
        if (!active) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const track = stream.getVideoTracks()[0];
        trackRef.current = track;
        if (videoRef.current) videoRef.current.srcObject = stream;

        // Detect hardware zoom support
        try {
          const caps = track.getCapabilities ? track.getCapabilities() : {};
          if (caps.zoom) {
            setZoomCaps({
              min: caps.zoom.min || 1,
              max: caps.zoom.max || 4,
              step: caps.zoom.step || 0.1,
            });
            setZoom(caps.zoom.min || 1);
          } else {
            // fallback digital zoom 1x..4x
            setZoomCaps({ min: 1, max: 4, step: 0.1, digital: true });
          }
        } catch {
          setZoomCaps({ min: 1, max: 4, step: 0.1, digital: true });
        }
      } catch (e) {
        setErr("Camera access denied or unavailable.");
      }
    })();
    return () => {
      active = false;
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      trackRef.current = null;
    };
  }, [open]);

  // Apply hardware zoom when supported
  useEffect(() => {
    const t = trackRef.current;
    if (!t || !zoomCaps || zoomCaps.digital) return;
    try {
      t.applyConstraints({ advanced: [{ zoom }] }).catch(() => {});
    } catch {
      /* ignore */
    }
  }, [zoom, zoomCaps]);

  const capture = () => {
    const video = videoRef.current;
    if (!video) return;
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) return;

    // Crop to 3:4 portrait from the centre of the video frame
    const targetRatio = 3 / 4; // width/height
    const currentRatio = vw / vh;
    let sx = 0, sy = 0, sw = vw, sh = vh;
    if (currentRatio > targetRatio) {
      // too wide → crop sides
      sw = vh * targetRatio;
      sx = (vw - sw) / 2;
    } else {
      // too tall → crop top/bottom
      sh = vw / targetRatio;
      sy = (vh - sh) / 2;
    }

    // Output canvas (3:4) — apply digital zoom by shrinking source rect
    const isDigital = zoomCaps?.digital && zoom > 1;
    if (isDigital) {
      const cropW = sw / zoom;
      const cropH = sh / zoom;
      sx = sx + (sw - cropW) / 2;
      sy = sy + (sh - cropH) / 2;
      sw = cropW;
      sh = cropH;
    }

    const outW = 1200; // 3:4 → 1200 x 1600
    const outH = Math.round(outW * (4 / 3));
    const canvas = document.createElement("canvas");
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, outW, outH);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
    onCapture(dataUrl);
    onClose();
  };

  if (!open) return null;

  return (
    <div
      data-testid="camera-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-2 sm:p-6"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md overflow-hidden rounded-2xl border border-heda-gold/30 bg-heda-ink shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-2.5">
          <div className="flex items-center gap-2 text-white">
            <Camera size={15} className="text-heda-gold" />
            <span className="font-display text-[15px] font-semibold">Take a photo</span>
          </div>
          <button
            data-testid="camera-close"
            onClick={onClose}
            className="rounded-full p-1.5 text-white/70 hover:bg-white/10 hover:text-white"
          >
            <X size={16} />
          </button>
        </div>

        {/* 3:4 portrait viewfinder */}
        <div className="relative aspect-[3/4] w-full overflow-hidden bg-black">
          {err ? (
            <div className="flex h-full items-center justify-center px-6 text-center text-sm text-white/80">
              {err}
            </div>
          ) : (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                transform:
                  zoomCaps?.digital && zoom > 1 ? `scale(${zoom})` : undefined,
                transformOrigin: "center center",
              }}
              className="h-full w-full object-cover"
            />
          )}

          {/* corner brackets */}
          <div className="pointer-events-none absolute inset-3">
            <span className="absolute left-0 top-0 h-5 w-5 border-l-2 border-t-2 border-heda-gold/80" />
            <span className="absolute right-0 top-0 h-5 w-5 border-r-2 border-t-2 border-heda-gold/80" />
            <span className="absolute bottom-0 left-0 h-5 w-5 border-b-2 border-l-2 border-heda-gold/80" />
            <span className="absolute bottom-0 right-0 h-5 w-5 border-b-2 border-r-2 border-heda-gold/80" />
          </div>

          {/* Zoom slider overlay */}
          {!err && zoomCaps && (
            <div className="absolute inset-x-4 bottom-3 flex items-center gap-2 rounded-full bg-black/55 px-3 py-1.5 backdrop-blur">
              <span className="text-[10px] font-semibold text-white/70">1×</span>
              <input
                data-testid="camera-zoom"
                type="range"
                min={zoomCaps.min}
                max={zoomCaps.max}
                step={zoomCaps.step}
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="heda-zoom-slider flex-1"
              />
              <span className="w-9 text-right text-[11px] font-bold text-heda-gold">
                {zoom.toFixed(1)}×
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-center border-t border-white/10 bg-heda-ink py-4">
          <button
            data-testid="camera-capture"
            onClick={capture}
            disabled={!!err}
            className="group relative h-16 w-16 rounded-full border-2 border-white/40 disabled:opacity-40"
            aria-label="Capture"
          >
            <span className="absolute inset-1.5 rounded-full bg-white transition group-active:scale-90 group-hover:bg-heda-gold" />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ----------------------- RENDER LOADER ---------------------------- */
const LOADER_STEPS = [
  { icon: Sparkles, label: "Reading your samples…" },
  { icon: Palette, label: "Mixing the palette…" },
  { icon: Sofa, label: "Arranging the furniture…" },
  { icon: Lamp, label: "Setting the lighting…" },
  { icon: Paintbrush, label: "Painting the walls…" },
  { icon: Frame, label: "Hanging the artwork…" },
  { icon: Camera, label: "Framing the shot…" },
  { icon: Wand2, label: "Working some magic…" },
];

// Site-image renders go through a different mental model — reading the real photo,
// imagining shutters, applying materials to those imagined shutters. Showing the
// staff this story makes the wait feel intentional instead of stuck.
const SITE_LOADER_STEPS = [
  { icon: Camera, label: "Reading your site photo…" },
  { icon: Sparkles, label: "Spotting open carcasses…" },
  { icon: Sofa, label: "Imagining the missing shutters…" },
  { icon: Palette, label: "Applying your materials…" },
  { icon: Paintbrush, label: "Finishing unfinished walls…" },
  { icon: Lamp, label: "Setting natural daylight…" },
  { icon: Frame, label: "Polishing the brass reveals…" },
  { icon: Wand2, label: "Watermarking + saving…" },
];

function RenderLoader({ headline, subline, siteMode = false, expectedSeconds = 25 }) {
  const [step, setStep] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const steps = siteMode ? SITE_LOADER_STEPS : LOADER_STEPS;
  useEffect(() => {
    const t0 = Date.now();
    const stepTimer = setInterval(() => {
      setStep((s) => (s + 1) % steps.length);
    }, 1400);
    const elapTimer = setInterval(() => {
      setElapsed(Math.floor((Date.now() - t0) / 1000));
    }, 250);
    return () => {
      clearInterval(stepTimer);
      clearInterval(elapTimer);
    };
  }, [steps.length]);

  // If recovery messages were passed, those take precedence (retry flow)
  if (headline) {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
        <Loader2 className="mb-3 animate-spin text-heda-gold" size={26} />
        <div
          data-testid="preview-headline"
          className="font-display text-[15px] font-semibold text-heda-ink transition-opacity duration-300"
        >
          {headline}
        </div>
        {subline && (
          <div
            data-testid="preview-subline"
            className="mt-1 text-[10.5px] text-heda-mute transition-opacity duration-300"
          >
            {subline}
          </div>
        )}
      </div>
    );
  }

  const Active = steps[step].icon;
  const label = steps[step].label;
  // Cap progress visually at 92% to never make the user think it's almost done
  // until the actual render arrives. Once past expectedSeconds, slow the climb.
  const rawPct = Math.min(0.92, elapsed / expectedSeconds);
  const overflow = Math.max(0, elapsed - expectedSeconds);
  // After expected ETA, drift slowly toward 92% so user knows we haven't given up
  const pct = Math.round((rawPct + Math.min(0.05, overflow / 200)) * 100);
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
      <div
        key={step}
        data-testid="loader-icon"
        className="mb-2 inline-flex h-11 w-11 items-center justify-center rounded-full bg-heda-gold/12 text-heda-gold animate-loaderPop"
      >
        <Active size={22} strokeWidth={1.8} />
      </div>
      <div
        key={`label-${step}`}
        data-testid="loader-label"
        className="font-display text-[13px] font-semibold text-heda-ink animate-loaderFade"
      >
        {label}
      </div>

      {/* Progress bar — real elapsed time, clamped to 92% */}
      <div className="mt-3 h-1 w-40 overflow-hidden rounded-full bg-heda-ink/10">
        <div
          className="h-full rounded-full bg-heda-gold transition-all duration-300 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="mt-1.5 text-[10px] text-heda-mute">
        {elapsed}s elapsed · usually {siteMode ? "20–35" : "15–25"} seconds
        {elapsed > expectedSeconds + 10 && (
          <span className="block text-[9.5px] text-heda-mute/80">
            Google&apos;s AI is a bit busy — sticking with it. Hang on.
          </span>
        )}
      </div>
    </div>
  );
}

/* --------------------------- PREVIEW ------------------------------ */
function Preview({
  loading,
  image,
  imageId,
  onReset,
  onDownload,
  onOpen,
  headline,
  subline,
  onVariations,
  variations,
  pickVariation,
  variationsError,
  siteMode = false,
}) {
  const variationsLoading = variations === "loading";
  const variationsList = Array.isArray(variations) ? variations : null;
  // While streaming progressively (1 then 2 then 3), `variations` is an array
  // but smaller than 3 — treat it as "still rolling" so empty slots show
  // shimmer placeholders alongside the completed renders.
  const variationsStreaming = variationsList && variationsList.length < 3;
  return (
    <div
      data-testid="preview-stage"
      className="relative flex flex-1 flex-col items-center justify-center rounded-2xl border border-heda-line bg-heda-cream p-4"
    >
      {loading ? (
        <div data-testid="preview-loading" className="flex w-full flex-col items-center gap-3">
          <div className="relative aspect-video w-full overflow-hidden rounded-xl">
            <div className="shimmer-bg absolute inset-0 animate-shimmer" />
            <RenderLoader headline={headline} subline={subline} siteMode={siteMode} expectedSeconds={siteMode ? 28 : 22} />
          </div>
        </div>
      ) : variationsLoading || variationsStreaming ? (
        <div data-testid="variations-loading" className="flex w-full flex-col items-center gap-3">
          <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-3">
            {[0, 1, 2].map((i) => {
              const done = variationsList && variationsList[i];
              const active = !done && variationsList && variationsList.length === i;
              return (
                <div
                  key={i}
                  className="relative aspect-video overflow-hidden rounded-xl bg-black/5 ring-1 ring-heda-line/60"
                >
                  {done ? (
                    <button
                      type="button"
                      data-testid={`variation-${i}`}
                      onClick={() => pickVariation(done)}
                      className="absolute inset-0 h-full w-full"
                      title="Tap to pick this one"
                    >
                      <img
                        src={done.src}
                        alt={`Take ${i + 1}`}
                        className="h-full w-full object-cover"
                      />
                      <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-heda-ink/85 px-2 py-[3px] text-[10px] font-semibold text-white shadow">
                        <CheckCircle2 size={11} className="text-heda-gold" />
                        Take {i + 1}
                      </span>
                    </button>
                  ) : (
                    <>
                      <div className="shimmer-bg absolute inset-0 animate-shimmer" />
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-center">
                        {active ? (
                          <>
                            <Loader2 size={18} className="animate-spin text-heda-gold" />
                            <div className="text-[11px] font-semibold text-heda-ink">
                              Take {i + 1} cooking…
                            </div>
                          </>
                        ) : (
                          <>
                            <Dices size={16} className="text-heda-mute" />
                            <div className="text-[10.5px] font-medium text-heda-mute">
                              Take {i + 1} queued
                            </div>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-2 flex items-center gap-2 text-center text-[12px] text-heda-mute">
            <Loader2 size={14} className="animate-spin text-heda-gold" />
            {variationsList && variationsList.length > 0
              ? `${variationsList.length} of 3 ready · ${3 - variationsList.length} more on the way — keep an eye out`
              : "Cooking 3 different takes one after another — first one in ~20s, all three in ~60s"}
          </div>
        </div>
      ) : variationsList ? (
        <div className="flex w-full flex-col items-center gap-3">
          <div className="flex items-center gap-2 self-start text-[12px] uppercase tracking-wider text-heda-mute">
            <Dices size={14} className="text-heda-gold" /> Pick your favourite of {variationsList.length}
          </div>
          <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-3">
            {variationsList.map((v, i) => (
              <button
                key={v.id || i}
                type="button"
                data-testid={`variation-${i}`}
                onClick={() => pickVariation(v)}
                className="group relative aspect-video w-full overflow-hidden rounded-xl bg-black shadow-md ring-1 ring-heda-line/60 transition hover:ring-heda-gold"
                title="Tap to pick this one"
              >
                <img
                  src={v.src}
                  alt={`Variation ${i + 1}`}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                />
                <span className="absolute right-1.5 top-1.5 flex items-center gap-1 rounded-full bg-black/55 px-2 py-0.5 text-[10px] font-medium text-white backdrop-blur opacity-0 transition group-hover:opacity-100">
                  <CheckCircle2 size={10} /> Pick
                </span>
              </button>
            ))}
          </div>
          <p className="mt-1 text-center text-[11px] text-heda-mute">
            All 3 are saved to the gallery. Pick one to show in the main preview.
          </p>
        </div>
      ) : image ? (
        <div className="flex w-full flex-col items-center gap-4">
          <button
            type="button"
            data-testid="result-open"
            onClick={onOpen}
            className="group relative aspect-video w-full overflow-hidden rounded-xl bg-black shadow-md ring-1 ring-heda-line/60"
            title="Click to open fullscreen"
          >
            <img
              data-testid="result-image"
              src={image}
              alt="Generated interior"
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
            />
            <span className="absolute right-2 top-2 flex items-center gap-1 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-medium text-white backdrop-blur transition opacity-90 group-hover:opacity-100">
              <Maximize2 size={11} /> Tap to view
            </span>
            {imageId && <FeedbackWidget renderId={imageId} position="bl" variant="dark" />}
          </button>
          {variationsError && (
            <div className="rounded-md bg-red-50 px-3 py-1.5 text-[11.5px] text-red-700">
              {variationsError}
            </div>
          )}
          <div className="flex flex-wrap items-center justify-center gap-2">
            <button
              data-testid="download-btn"
              onClick={onDownload}
              className="inline-flex items-center gap-1.5 rounded-full border border-heda-line bg-white px-4 py-2 text-sm font-medium text-heda-ink hover:border-heda-gold hover:text-heda-gold"
            >
              <Download size={14} /> Download
            </button>
            {onVariations && (
              <button
                data-testid="variations-btn"
                onClick={onVariations}
                title="Generate 3 variations of this scene · ~3× credits"
                className="inline-flex items-center gap-1.5 rounded-full border border-heda-line bg-white px-4 py-2 text-sm font-medium text-heda-ink/80 hover:border-heda-gold hover:text-heda-gold"
              >
                <Dices size={14} /> 3 Variations
              </button>
            )}
            <button
              data-testid="reset-btn"
              onClick={onReset}
              className="inline-flex items-center gap-1.5 rounded-full bg-heda-ink px-4 py-2 text-sm font-medium text-white hover:bg-black"
            >
              <RefreshCw size={14} /> Create New
            </button>
          </div>
        </div>
      ) : (
        <div data-testid="preview-empty" className="flex aspect-video w-full flex-col items-center justify-center text-center">
          <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-2xl bg-heda-olive-tint/60 text-heda-olive">
            <ImageIcon size={36} strokeWidth={1.2} />
          </div>
          <div className="font-display text-xl font-semibold text-heda-ink">Your Design Preview</div>
          <p className="mt-1 text-sm text-heda-mute">
            Select a scene, add products, and generate · 16:9
          </p>
        </div>
      )}
    </div>
  );
}

/* ---------------------------- APP -------------------------------- */
export default function App() {
  const { user, logout } = useAuth();
  const { triggerCreditAlert, clearCreditAlert } = useCreditAlert();
  const [scenes, setScenes] = useState([]);
  const [sceneId, setSceneId] = useState(null);
  const [products, setProducts] = useState([]);
  const [siteImage, setSiteImage] = useState(null);
  const [sitePins, setSitePins] = useState([]); // [{letter, x, y, id}] — surface markers on site image
  const [cameraTarget, setCameraTarget] = useState("product"); // "product" | "site"
  const [selectedStyles, setSelectedStyles] = useState([]);
  const [quality, setQuality] = useState("hero"); // "hero" | "fast"
  const [cameraOpen, setCameraOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState(null);
  const [error, setError] = useState("");
  const [showLightbox, setShowLightbox] = useState(false);
  const [imageId, setImageId] = useState(null);
  const [recovering, setRecovering] = useState(false);
  const [recoveryMsg, setRecoveryMsg] = useState(0);
  // Whether the current `error` is worth offering "Check Gallery" for
  // (only true for network/timeout/5xx — never for 4xx validation errors
  // where no render was ever created).
  const [errorIsRecoverable, setErrorIsRecoverable] = useState(false);
  // "Network is slow, your render is safe" banner — flips true when polling
  // has been running > 30s without a result. Reset on next generate.
  const [networkSlow, setNetworkSlow] = useState(false);

  // "Roll the Dice" — 3 parallel variations shown after main render is done
  const [variations, setVariations] = useState(null); // null | "loading" | [{src,id}, …]
  const [variationsError, setVariationsError] = useState("");

  // Rotate the recovery message every 4s while we're recovering
  useEffect(() => {
    if (!recovering) return;
    setRecoveryMsg(0);
    const id = setInterval(
      () => setRecoveryMsg((i) => (i + 1) % RECOVERY_LINES.length),
      4000
    );
    return () => clearInterval(id);
  }, [recovering]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/scenes`);
        const data = await res.json();
        setScenes(data);
        if (data.length && !sceneId) setSceneId(data[0].id);
      } catch {
        setError("Unable to reach backend.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleStyle = (id) =>
    setSelectedStyles((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );

  const addProducts = (imgs) =>
    setProducts((prev) => {
      const existing = prev;
      const next = [...existing];
      for (const src of imgs) {
        if (next.length >= 4) break;
        next.push({ src, role: "accent", scale_type: "cutout" });
      }
      // Ensure exactly one "main" — default first if none
      if (next.length > 0 && !next.some((p) => p.role === "main")) {
        next[0] = { ...next[0], role: "main" };
      }
      return next;
    });

  const removeProduct = (idx) =>
    setProducts((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      if (next.length > 0 && !next.some((p) => p.role === "main")) {
        next[0] = { ...next[0], role: "main" };
      }
      // Drop any site pins whose letter index is no longer valid.
      const validLetters = new Set(next.map((_, i) => String.fromCharCode(65 + i)));
      setSitePins((p) => p.filter((pin) => validLetters.has(pin.letter)));
      return next;
    });

  const setMain = (idx) =>
    setProducts((prev) =>
      prev.map((p, i) => ({ ...p, role: i === idx ? "main" : "accent" }))
    );

  const setScale = (idx, scale_type) =>
    setProducts((prev) =>
      prev.map((p, i) => (i === idx ? { ...p, scale_type } : p))
    );

  const handleReset = () => {
    setImage(null);
    setImageId(null);
    setVariations(null);
    setVariationsError("");
    setError("");
  };

  const handleDownload = () => {
    if (!image) return;
    const a = document.createElement("a");
    a.href = image;
    a.download = `hedaply-${sceneId || "render"}-${Date.now()}.jpg`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleGenerate = useCallback(async () => {
    setError("");
    setErrorIsRecoverable(false);
    if (!sceneId) return setError("Pick a scene first.");
    if (products.length === 0) return setError("Add at least one product sample.");
    setLoading(true);
    setImage(null);

    const startedAt = new Date(Date.now() - 5000).toISOString(); // -5s clock skew
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 180000); // 3 min safety timeout

    // If the request fails / aborts, the backend may still be finishing in the
    // background (proxies often drop slow connections at ~100s while the
    // backend finishes the Gemini call at ~110-130s). Poll the gallery for a
    // fresh render by THIS user matching THIS scene and recover it.
    //
    // Window extended to 180s (was 60s) because in real production we've seen
    // the backend save the render up to 90-110s after the proxy drop. A 60s
    // recovery window was giving up before the row landed in MongoDB, which
    // is what was causing "frontend says fail but render shows in gallery"
    // — the #1 user complaint.
    const recover = async () => {
      setRecovering(true);
      try {
        const myId = user?.id;
        const myEmail = user?.email;
        const deadline = Date.now() + 180000; // 180s — covers worst-case backend completion + post-process
        while (Date.now() < deadline) {
          try {
            const r = await fetch(
              `${API}/renders?since=${encodeURIComponent(startedAt)}&limit=20`,
              { headers: authHeaders() }
            );
            if (r.ok) {
              const data = await r.json();
              const mine = (data.items || []).find(
                (x) =>
                  x.scene_id === sceneId &&
                  ((myId && x.created_by_id === myId) ||
                    (myEmail && x.created_by_email === myEmail))
              );
              if (mine) {
                const full = await fetch(`${API}/renders/${mine.id}`, {
                  headers: authHeaders(),
                });
                if (full.ok) {
                  const fdata = await full.json();
                  if (fdata.image) return { src: fdata.image, id: mine.id };
                }
              }
            }
          } catch {
            /* ignore — keep polling */
          }
          await new Promise((res) => setTimeout(res, 4000));
        }
        return null;
      } finally {
        setRecovering(false);
      }
    };

    // Async-job pattern (Feb 2026): POST /api/generate returns a job_id
    // within ~1s, then we poll /api/generate/jobs/{job_id} until status
    // flips to "done" or "failed". This eliminates Cloudflare's 100s edge
    // timeout from the failure modes — even a 120s generation completes
    // because the original POST request returned 1s in.
    //
    // Aug 2026 exhibition-day hardening:
    //  - Poll interval 2.5s → 1.5s (cuts perceived latency by ~1-2s per render)
    //  - POST auto-retries 3× on network failure (flaky exhibition WiFi)
    //  - Slow-network banner shows after 30s of polling without a result
    setNetworkSlow(false);
    const POLL_INTERVAL_MS = 1500;
    const POLL_MAX_MS = 200000; // 200s — exceeds backend's 180s stale-job auto-fail
    const SLOW_NETWORK_THRESHOLD_MS = 30000;
    // ONE key per user action — reused across all 3 retries inside startJob
    // so a retry after a slow-WiFi abort maps to the SAME billed job.
    const idemKey = newIdemKey();

    const startJob = async () => {
      // 3 attempts with 1s backoff — protects against exhibition-day flaky WiFi
      // where the very first POST might drop. Each attempt has its own 45s
      // timeout so the base64 product-image upload (~1-2 MB) has plenty of
      // headroom on slow exhibition connections. The backend responds in
      // <1s once the body lands, so most of the budget is for the upload.
      let lastErr = null;
      for (let attempt = 1; attempt <= 3; attempt++) {
        const attemptCtrl = new AbortController();
        const attemptTimer = setTimeout(() => attemptCtrl.abort(), 45000);
        try {
          const res = await fetch(`${API}/generate`, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...authHeaders() },
            signal: attemptCtrl.signal,
            body: JSON.stringify({
              scene_id: sceneId,
              products: products.map((p) => ({
                image_base64: p.src,
                role: p.role || "accent",
                scale_type: p.scale_type || "cutout",
              })),
              styles: selectedStyles,
              site_image_base64: siteImage || undefined,
              site_pins: siteImage && sitePins.length > 0 ? sitePins : undefined,
              quality,
              idempotency_key: idemKey,
            }),
          });
          clearTimeout(attemptTimer);
          let data = null;
          try { data = await res.json(); } catch { /* non-JSON */ }
          // 4xx (validation) → don't retry — same error would happen again
          if (res.status >= 400 && res.status < 500) return { res, data };
          if (res.ok) return { res, data };
          // 5xx → retry after brief pause
          lastErr = new Error((data && data.detail) || `Server error ${res.status}`);
        } catch (e) {
          clearTimeout(attemptTimer);
          lastErr = e;
        }
        if (attempt < 3) {
          // Show slow-network hint even during POST retries
          if (attempt >= 2) setNetworkSlow(true);
          await new Promise((r) => setTimeout(r, 1000 * attempt));
        }
      }
      throw lastErr || new Error("Couldn't start render. Please try again.");
    };

    const pollJob = async (jobId) => {
      const startedAt = Date.now();
      const deadline = startedAt + POLL_MAX_MS;
      while (Date.now() < deadline) {
        // Flip on the slow-network banner once we've been polling >30s
        if (!networkSlow && Date.now() - startedAt > SLOW_NETWORK_THRESHOLD_MS) {
          setNetworkSlow(true);
        }
        try {
          const r = await fetch(`${API}/generate/jobs/${jobId}`, {
            headers: authHeaders(),
          });
          if (r.ok) {
            const d = await r.json();
            if (d.status === "done") {
              return { ok: true, image: d.image, id: d.id || jobId };
            }
            if (d.status === "failed") {
              return { ok: false, error: d.error || "Render failed. Please try again." };
            }
            // Still pending — keep polling
          } else if (r.status === 401) {
            logout();
            return { ok: false, error: "Session expired. Please sign in again." };
          }
          // Any other status (503, 502, network blip) → keep polling.
          // The render is still running on the backend regardless.
        } catch {
          // Network blip → surface the slow banner and keep polling
          setNetworkSlow(true);
        }
        await new Promise((res) => setTimeout(res, POLL_INTERVAL_MS));
      }
      return { ok: false, error: "Render took too long. Please try again." };
    };

    try {
      // Step 1: start the job. This call returns in ~1s; the only failure
      // modes are validation errors (400/413), auth (401), or backend down.
      const { res, data } = await startJob();
      if (!res.ok) {
        if (res.status === 402) triggerCreditAlert();
        if (res.status === 401) {
          logout();
          throw new Error("Session expired. Please sign in again.");
        }
        if (res.status === 413) {
          const err413 = new Error("Images too large. Please retake in lower resolution.");
          err413.httpStatus = 413;
          throw err413;
        }
        const httpErr = new Error((data && data.detail) || `Request failed (${res.status}).`);
        httpErr.httpStatus = res.status;
        throw httpErr;
      }
      const jobId = data && data.job_id;
      if (!jobId) {
        throw new Error("Couldn't start render. Please try again.");
      }

      // Step 2: poll until done / failed / timeout
      const result = await pollJob(jobId);
      if (!result.ok) {
        throw new Error(result.error);
      }
      setImage(result.image);
      setImageId(result.id || null);
      clearCreditAlert();
    } catch (e) {
      // Network drop / abort / 5xx already handled — try gallery recovery once more
      const isNetwork =
        e.name === "AbortError" ||
        e.message === "Failed to fetch" ||
        e.message === "Load failed" ||
        e.message === "Render service busy. Please try again in a moment.";
      if (isNetwork) {
        const recovered = await recover();
        if (recovered) {
          setImage(recovered.src);
          setImageId(recovered.id);
          return;
        }
      }
      if (e.name === "AbortError") {
        setError("Took too long. Please try again — fewer/smaller samples help.");
        setErrorIsRecoverable(true);
      } else if (e.message === "Failed to fetch" || e.message === "Load failed") {
        setError("Network issue — check your internet and try again.");
        setErrorIsRecoverable(true);
      } else {
        setError(e.message || "Something went wrong. Please try again.");
        // "Check Gallery" is meaningful only when a render MAY exist server-side.
        // That happens on 5xx / timeout / abort — never on 4xx validation. When
        // no HTTP status is attached (e.g. thrown from pollJob's timeout), fall
        // back to the string heuristic.
        const status = e.httpStatus;
        if (typeof status === "number") {
          setErrorIsRecoverable(status >= 500);
        } else {
          const isServerish =
            /took too long|network|render service busy|couldn't complete|couldn't start|server error/i.test(
              e.message || ""
            );
          setErrorIsRecoverable(isServerish);
        }
      }
    } finally {
      clearTimeout(timer);
      setLoading(false);
      setNetworkSlow(false);
    }
  }, [sceneId, products, selectedStyles, logout, user, triggerCreditAlert, clearCreditAlert, siteImage, sitePins, quality]);

  const canGenerate = sceneId && products.length > 0 && !loading;

  const handleEdit = useCallback(
    async (instruction) => {
      if (!image) return null;
      let res;
      try {
        res = await resilientFetch(`${API}/edit`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify({
            image_base64: image,
            instruction,
            scene_id: sceneId,
            parent_id: imageId,
          }),
        });
      } catch (netErr) {
        throw new Error("Network hiccup. Please check your connection and try again.");
      }
      if (res.status === 401) {
        logout();
        throw new Error("Session expired. Please sign in again.");
      }
      if (res.status === 402) triggerCreditAlert();
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success || !data.image) {
        throw new Error(data.detail || "Edit failed. Try again.");
      }
      setImage(data.image);
      setImageId(data.id || null);
      clearCreditAlert();
      return data.image;
    },
    [image, imageId, sceneId, logout, triggerCreditAlert, clearCreditAlert]
  );

  const handleVariations = useCallback(async () => {
    if (!sceneId || products.length === 0 || variations === "loading") return;
    setVariations("loading");
    setVariationsError("");

    // Three distinct "creative seeds" so Gemini produces visibly different
    // renders rather than near-duplicates of the same scene.
    const variationSeeds = [
      "Variation 1 of 3 — emphasise warm golden-hour lighting and a wider establishing camera angle.",
      "Variation 2 of 3 — emphasise cooler late-evening lamp pools and a closer 3/4 hero angle.",
      "Variation 3 of 3 — emphasise crisp daylight and a slightly elevated camera with deeper depth-of-field.",
    ];

    const callOne = async (seed) => {
      // One key per variation — the 3 variations are 3 distinct renders,
      // so they must get 3 distinct keys. Any retry of THIS variation
      // (inside resilientFetch) will map back to the same billed job.
      const idemKey = newIdemKey();
      const body = JSON.stringify({
        scene_id: sceneId,
        products: products.map((p) => ({
          image_base64: p.src,
          role: p.role || "accent",
          scale_type: p.scale_type || "cutout",
        })),
        styles: selectedStyles,
        site_image_base64: siteImage || undefined,
        site_pins: siteImage && sitePins.length > 0 ? sitePins : undefined,
        quality,
        variation_seed: seed,
        idempotency_key: idemKey,
      });
      try {
        // Step 1: start the async job
        const r = await resilientFetch(`${API}/generate`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body,
        });
        if (!r.ok) {
          if (r.status === 402) triggerCreditAlert();
          const d = await r.json().catch(() => null);
          return { ok: false, detail: d && d.detail };
        }
        const startData = await r.json().catch(() => null);
        const jobId = startData && startData.job_id;
        if (!jobId) return { ok: false };

        // Step 2: poll until done / failed (200s deadline matches backend stale-job auto-fail)
        const deadline = Date.now() + 200000;
        while (Date.now() < deadline) {
          await new Promise((res) => setTimeout(res, 2500));
          try {
            const p = await fetch(`${API}/generate/jobs/${jobId}`, {
              headers: authHeaders(),
            });
            if (p.ok) {
              const d = await p.json();
              if (d.status === "done") {
                return { ok: true, src: d.image, id: d.id || jobId };
              }
              if (d.status === "failed") {
                return { ok: false, detail: d.error };
              }
            }
          } catch {
            // network blip → keep polling
          }
        }
        return { ok: false, detail: "Variation took too long." };
      } catch {
        return { ok: false };
      }
    };

    // Run the 3 variations SEQUENTIALLY but stream results PROGRESSIVELY —
    // each completed render appears immediately instead of staff staring at
    // a blank loader for 60–90s. Sequential is required (parallel hits the
    // 60s ingress timeout); progressive display fixes the "nothing ever
    // shows" perception.
    const accumulated = [];
    for (let i = 0; i < 3; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      const r = await callOne(variationSeeds[i]);
      if (r.ok) {
        accumulated.push(r);
        // Update state so partial results render right away (1, then 2, then 3)
        setVariations([...accumulated]);
      }
    }
    if (accumulated.length === 0) {
      setVariations(null);
      setVariationsError("Render service is paused. Please try again shortly.");
      return;
    }
    clearCreditAlert();
  }, [sceneId, products, selectedStyles, variations, triggerCreditAlert, clearCreditAlert, siteImage, quality]);

  const pickVariation = (v) => {
    setImage(v.src);
    setImageId(v.id);
    setVariations(null);
  };

  return (
    <div className="min-h-screen bg-heda-cream text-heda-ink">
      <Header user={user} onLogout={logout} />
      <RetryToast />

      <main className="mx-auto w-full max-w-6xl px-4 pb-10 md:px-8 md:pb-14">
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 lg:gap-6">
          {/* LEFT CARD — Create Design */}
          <section
            data-testid="controls-card"
            className="rounded-3xl bg-white p-5 shadow-[0_1px_0_rgba(0,0,0,0.02),0_10px_30px_-20px_rgba(0,0,0,0.15)] ring-1 ring-heda-line/70 md:p-7"
          >
            <div className="mb-5 flex items-center gap-2">
              <Sparkles size={18} className="text-heda-gold" />
              <h2 className="font-display text-[20px] font-bold text-heda-ink">
                Create Design
              </h2>
            </div>

            <div className="hairline mb-5" />

            {/* When a site image is present, the scene picker is irrelevant
                — the room geometry is already in the photo. Dim + disable it
                so staff understands and doesn't waste a click. */}
            <div className={siteImage ? "pointer-events-none opacity-40" : ""}>
              <SceneGrid scenes={scenes} value={sceneId} onChange={setSceneId} />
              {siteImage && (
                <div className="mt-1 text-[10.5px] text-heda-mute">
                  Scene picker hidden — your site image is the scene.
                </div>
              )}
            </div>

            <div className="mt-7">
              <SiteSample
                site={siteImage}
                onSet={(img) => { setSiteImage(img); setSitePins([]); }}
                onClear={() => { setSiteImage(null); setSitePins([]); }}
                onOpenCamera={() => {
                  setCameraTarget("site");
                  setCameraOpen(true);
                }}
              />
              <ProductSamples
                products={products}
                onAdd={addProducts}
                onRemove={removeProduct}
                onSetMain={setMain}
                onSetScale={setScale}
                onOpenCamera={() => {
                  setCameraTarget("product");
                  setCameraOpen(true);
                }}
                siteActive={!!siteImage}
              />
              <SitePinDesigner
                siteImage={siteImage}
                products={products}
                pins={sitePins}
                setPins={setSitePins}
              />
            </div>

            <div className="mt-7">
              <div className="mb-3 text-[14px] text-heda-ink/90">
                Design Style{" "}
                <span className="text-heda-mute">(optional - select one or more)</span>
              </div>
              <StyleGrid selected={selectedStyles} onToggle={toggleStyle} />
            </div>

            {error && (
              <div
                data-testid="error-box"
                className="mt-5 flex flex-col gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-[13px] text-red-700"
              >
                <span>{error}</span>
                {errorIsRecoverable && (
                  <Link
                    to="/admin"
                    data-testid="error-check-gallery-btn"
                    className="self-start rounded-full border border-red-300 bg-white px-3 py-1 text-[12px] font-semibold text-red-700 transition-colors hover:bg-red-100"
                  >
                    Check Gallery — your render may have landed there
                  </Link>
                )}
              </div>
            )}

            {/* Speed toggle — Nano Banana 2 (Fast, ~15-25s) vs Gemini 3 Pro (Hero, ~25-35s).
                Fast trades some material fidelity for speed; Hero is magazine-grade.
                Defaults to Hero. */}
            <div className="mt-6 flex items-center justify-between rounded-2xl border border-heda-line bg-white px-4 py-2.5">
              <div className="flex flex-col">
                <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-heda-mute">
                  Quality
                </span>
                <span className="text-[12px] text-heda-ink">
                  {quality === "fast" ? "Fast · ~15-25 seconds" : "Hero · ~25-35 seconds"}
                </span>
              </div>
              <div
                role="tablist"
                aria-label="Render quality"
                className="flex rounded-full bg-heda-cream p-1"
              >
                <button
                  type="button"
                  role="tab"
                  data-testid="quality-hero-btn"
                  aria-selected={quality === "hero"}
                  onClick={() => setQuality("hero")}
                  className={[
                    "rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wider transition-all",
                    quality === "hero" ? "bg-heda-ink text-white" : "text-heda-mute hover:text-heda-ink",
                  ].join(" ")}
                >
                  Hero
                </button>
                <button
                  type="button"
                  role="tab"
                  data-testid="quality-fast-btn"
                  aria-selected={quality === "fast"}
                  onClick={() => setQuality("fast")}
                  className={[
                    "rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wider transition-all",
                    quality === "fast" ? "bg-heda-ink text-white" : "text-heda-mute hover:text-heda-ink",
                  ].join(" ")}
                >
                  Fast
                </button>
              </div>
            </div>

            <button
              data-testid="generate-btn"
              disabled={!canGenerate}
              onClick={handleGenerate}
              className={[
                "mt-3 flex w-full items-center justify-center gap-2 rounded-2xl px-6 py-4 text-[15px] font-semibold tracking-wide transition-all",
                canGenerate
                  ? "bg-heda-olive text-white shadow-[0_10px_24px_-10px_rgba(107,122,61,.5)] hover:bg-heda-olive-dark"
                  : "cursor-not-allowed bg-heda-olive/45 text-white/90",
              ].join(" ")}
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Rendering…
                </>
              ) : (
                <>
                  <Sparkles size={16} /> Generate Design
                </>
              )}
            </button>
          </section>

          {/* RIGHT CARD — Preview */}
          <section
            data-testid="preview-card"
            className="flex flex-col rounded-3xl bg-white p-5 shadow-[0_1px_0_rgba(0,0,0,0.02),0_10px_30px_-20px_rgba(0,0,0,0.15)] ring-1 ring-heda-line/70 md:p-7"
          >
            <div className="mb-5 flex items-center gap-2">
              <ImageIcon size={18} className="text-heda-gold" />
              <h2 className="font-display text-[20px] font-bold text-heda-ink">
                Preview
              </h2>
            </div>
            <div className="hairline mb-5" />
            <Preview
              loading={loading}
              image={image}
              imageId={imageId}
              onReset={handleReset}
              onDownload={handleDownload}
              onOpen={() => setShowLightbox(true)}
              headline={
                networkSlow && loading
                  ? "Network is slow — your render is safe"
                  : recovering
                  ? RECOVERY_LINES[recoveryMsg].h
                  : undefined
              }
              subline={
                networkSlow && loading
                  ? "Hang tight. The AI is still working. We'll show it the moment it's ready."
                  : recovering
                  ? RECOVERY_LINES[recoveryMsg].s
                  : undefined
              }
              onVariations={siteImage ? undefined : handleVariations}
              variations={variations}
              pickVariation={pickVariation}
              variationsError={variationsError}
              siteMode={Boolean(siteImage)}
            />
          </section>
        </div>
      </main>

      <footer className="pb-8 text-center text-[12px] text-heda-mute">
        Powered by <span className="font-semibold text-heda-ink/80">Heda Plywoods</span>
      </footer>

      <CameraModal
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={async (d) => {
          const c = await compressImage(d);
          if (cameraTarget === "site") {
            setSiteImage(c);
            setCameraTarget("product");
          } else {
            addProducts([c]);
          }
        }}
      />

      <Lightbox
        open={showLightbox && !!image}
        src={image}
        caption="Hedaply AI · Tap outside or press Esc to close"
        onClose={() => setShowLightbox(false)}
        onDownload={handleDownload}
        onEdit={handleEdit}
        renderId={imageId}
      />
    </div>
  );
}
